import { EMPTY_FORM } from './prescriptionForm';
// Imported rather than restated: the week is read Monday-first in exactly one
// place, and a second copy of that order would drift the first time somebody
// changed their mind about where the weekend goes.
import { WEEK_ORDER, SHORT_DAY } from './weekBuild';

// Adding work to a week without leaving it.
//
// # WHY THE FORM STARTS PRE-FILLED
//
// A coach building a block adds eight to twelve exercises in a sitting, and the
// prescription is usually a small edit of the last one — same tempo, same rest,
// series moving A1 → A2 → B1. Starting from blank means retyping "4-0-1-0"
// twelve times, which is how a fast surface becomes a slow one.
//
// So the form opens carrying the previous line's shape, with the series
// advanced. That is a guess, and it is visible and editable before anything is
// saved — which is the difference between a helpful default and a silent one.

/** The next series label after this one: A1 → A2, A2 → A3, and B1 opens a group. */
export function nextSeries(series) {
  const match = /^([A-Z])([0-9]{1,2})$/.exec(String(series ?? '').trim());
  if (!match) return 'A1';

  const [, letter, index] = match;
  const position = Number(index);
  // Stops at 9 rather than rolling into a new letter: A10 is legal in the
  // grammar but nobody writes it, and rolling to B1 would silently start a new
  // superset group — a structural change the coach did not ask for.
  if (position >= 9) return `${letter}9`;
  return `${letter}${position + 1}`;
}

/** The letter after this one: A1 → B1. What "start a new group" means. */
export function nextGroup(series) {
  const match = /^([A-Z])[0-9]{1,2}$/.exec(String(series ?? '').trim());
  if (!match) return 'A1';
  const letter = match[1];
  if (letter === 'Z') return 'Z1';
  return `${String.fromCharCode(letter.charCodeAt(0) + 1)}1`;
}

/**
 * The form a new line should open with, given what is already on the day.
 *
 * An empty day starts at A1 with the shared defaults. A day with work carries
 * the last line's tempo and rest forward and advances the series, because the
 * next thing a coach adds is usually the partner of the thing they just added.
 */
export function formForNextLine(lines) {
  // Defaulted BEFORE the index, not inside it: `(lines ?? [])[lines.length - 1]`
  // still dereferences the original null to get the length.
  const written = lines ?? [];
  const previous = written[written.length - 1];
  if (!previous) return { ...EMPTY_FORM };

  return {
    ...EMPTY_FORM,
    series: nextSeries(previous.series),
    sets: String(previous.sets ?? EMPTY_FORM.sets),
    reps: String(previous.repScheme ?? EMPTY_FORM.reps),
    tempo: previous.tempo || EMPTY_FORM.tempo,
    rest: String(previous.rest ?? EMPTY_FORM.rest),
  };
}

/**
 * What a new session on an empty day should be called.
 *
 * The weekday, because it is true and it is one fewer decision. A coach who
 * wants "Lower A" renames it; a coach who does not gets a name that is never
 * wrong.
 */
export function defaultSessionName(day) {
  return day?.label ? `${day.label}` : 'Session';
}

/**
 * How the balance moves if this line is added.
 *
 * # WHY THIS IS PREDICTED AND NOT JUST REFETCHED
 *
 * It is refetched too — the server stays the authority. But the strip is the
 * reason to build in this view at all, and a coach adding a fourth pressing
 * movement should see the pull side go thin as they type, not a second later
 * once a round trip lands. The prediction is discarded the moment the real
 * answer arrives.
 *
 * @returns {{pattern: string, sets: number}|null}
 */
export function predictedAddition(exercise, form) {
  if (!exercise) return null;
  const sets = Number(form?.sets);
  if (!Number.isFinite(sets) || sets <= 0) return null;
  return { pattern: exercise.pattern || 'unclassified', sets };
}

/** Applies a predicted addition to a week, for display only. */
export function withPrediction(week, addition) {
  if (!week || !addition) return week;

  const patterns = (week.patterns ?? []).map((p) => ({ ...p }));
  const existing = patterns.find((p) => p.pattern === addition.pattern);
  if (existing) {
    existing.sets += addition.sets;
    existing.exercises += 1;
  } else {
    patterns.push({
      pattern: addition.pattern,
      label: addition.pattern,
      sets: addition.sets,
      exercises: 1,
    });
  }

  const setsFor = (pattern) => patterns.find((p) => p.pattern === pattern)?.sets ?? 0;
  const balance = (week.balance ?? []).map((pair) => {
    const aSets = setsFor(pair.a);
    const bSets = setsFor(pair.b);
    const heavier = Math.max(aSets, bSets);
    const lighter = Math.min(aSets, bSets);
    // The same rule the server applies — six sets before judging, half as the
    // threshold. Restated here only for the moment between typing and saving;
    // models/pattern.go remains the definition.
    const thin = heavier >= 6 && lighter < heavier * 0.5
      ? (aSets < bSets ? pair.a : pair.b)
      : '';
    return { ...pair, aSets, bSets, thin };
  });

  return {
    ...week,
    patterns,
    balance,
    sets: (Number(week.sets) || 0) + addition.sets,
  };
}

/**
 * How a line's intensity reads on the column, in as few characters as possible.
 *
 * # WHY IT IS NOT RESOLVED
 *
 * The week is a planning view. A percentage resolves against somebody's max, and
 * a block assigned to twelve athletes has twelve of those — so the column shows
 * what the coach WROTE, and the athlete's own screens show what it works out to.
 *
 * Returns "" rather than a placeholder when nothing was prescribed: a
 * prescription with no intensity is valid and common, and "—" against every
 * accessory movement is noise.
 */
export function intensityOf(line) {
  if (!line) return '';
  if (line.percentOneRM != null) return `${line.percentOneRM}% 1RM`;
  if (line.load != null) return `${line.load} ${line.unit || ''}`.trim();
  if (line.rpe != null) return `RPE ${line.rpe}`;
  if (line.rir != null) return `${line.rir} RIR`;
  return '';
}

/**
 * Where a day can be moved or copied to.
 *
 * # WHY THIS IS A MENU AND NOT DRAG-AND-DROP
 *
 * Dragging is the obvious answer and the wrong one here. HTML5 drag events do
 * not fire on touch at all, and this is a phone-first app — a coach writing a
 * block on the way to the gym would get a feature that silently does nothing.
 * It is also invisible to a keyboard and to a screen reader unless a parallel
 * mechanism exists anyway, which is this one.
 *
 * A menu works on every input, says what it will do before it does it, and is
 * one tap rather than a drag across a scrolling strip. Dragging can be layered
 * on top for a mouse later; it cannot be the only way.
 *
 * The current day is excluded: copying Monday to Monday is not a thing anybody
 * means, and moving it there is a no-op dressed as an action.
 */
export function dayChoices(week, currentDay) {
  return (week?.days ?? [])
    .filter((day) => day.dayOfWeek !== currentDay)
    .sort((a, b) => WEEK_ORDER.indexOf(a.dayOfWeek) - WEEK_ORDER.indexOf(b.dayOfWeek))
    .map((day) => ({
      dayOfWeek: day.dayOfWeek,
      label: day.label,
      short: SHORT_DAY[day.dayOfWeek],
      // Said on the menu item, because "copy to Thursday" reads very differently
      // when Thursday already has nine sets on it.
      occupied: (day.sessions ?? []).length > 0,
    }));
}

/**
 * The body for moving a session to another day.
 *
 * PUT is a full replace, so everything the session should keep has to be sent —
 * omitting the phase would detach it from the block and the day would vanish
 * from the view it was moved in.
 */
export function moveRequest(session, phaseId, dayOfWeek) {
  return {
    name: session?.name ?? '',
    order: Number(session?.order) || 1,
    phaseId: Number(phaseId),
    dayOfWeek,
  };
}
